import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api/client';
import type { User } from '@shared/types';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const initialized = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isApprovedClient = computed(
    () => user.value?.role === 'client' && user.value.status === 'approved'
  );
  const isPending = computed(() => user.value?.status === 'pending');

  async function fetchMe() {
    loading.value = true;
    try {
      const res = await api.get<{ user: User }>('/auth/me');
      if (res.ok && res.data) {
        user.value = res.data.user;
      } else {
        user.value = null;
      }
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function login(email: string, password: string) {
    const res = await api.post<{ user: User }>('/auth/login', { email, password });
    if (res.ok && res.data) {
      user.value = res.data.user;
      return res.data;
    }
    throw new Error(res.error || 'Login failed');
  }

  // Listen for global 401 events from the API client. If a session
  // expires (e.g. after a long idle period or a server restart that
  // invalidated in-memory session tokens), the user is silently
  // logged out and routed back to the login screen instead of seeing
  // a "Something went wrong" overlay on every subsequent request.
  if (typeof window !== 'undefined') {
    window.addEventListener('umail24:unauthorized', () => {
      if (user.value) {
        user.value = null;
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    });
  }

  async function register(email: string, password: string, full_name?: string) {
    const res = await api.post<{ status: string; message: string }>('/auth/register', {
      email,
      password,
      full_name,
    });
    if (!res.ok) throw new Error(res.error || 'Registration failed');
    return res.data!;
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    user.value = null;
  }

  async function updateProfile(full_name: string) {
    const res = await api.patch<{ user: User }>('/auth/me', { full_name });
    if (res.ok && res.data) user.value = res.data.user;
  }

  async function changePassword(current: string, next: string) {
    const res = await api.post('/auth/me/password', { current, next });
    if (!res.ok) throw new Error(res.error || 'Password change failed');
  }

  function handleSocketStatus(payload: { user_id: string; status: string }) {
    if (user.value && payload.user_id === user.value.id) {
      user.value = { ...user.value, status: payload.status as any };
    }
  }

  return {
    user,
    loading,
    initialized,
    isAuthenticated,
    isAdmin,
    isApprovedClient,
    isPending,
    fetchMe,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    handleSocketStatus,
  };
});